import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumAlcoholType implements BaseEnumType {
  private static allValues: EnumAlcoholType[] = []; // make sure this is top-most
  
  public static none = new EnumAlcoholType(0, "None", "enum_alcohol_type_none");
  public static beer = new EnumAlcoholType(1, "Beer", "enum_alcohol_type_beer");
  public static stillCiderPerry = new EnumAlcoholType(2, "StillCiderPerry", "enum_alcohol_type_still_cider_perry");
  public static sparklingCiderPerry = new EnumAlcoholType(3, "SparklingCiderPerry", "enum_alcohol_type_sparkling_cider_perry");
  public static stillWine = new EnumAlcoholType(4, "StillWine", "enum_alcohol_type_still_wine");
  public static sparklingWine = new EnumAlcoholType(5, "SparklingWine", "enum_alcohol_type_sparkling_wine");
  public static spirits = new EnumAlcoholType(6, "Spirits", "enum_alcohol_type_spirits");
  public static scotchWhisky = new EnumAlcoholType(7, "ScotchWhisky", "enum_alcohol_type_scotch_whisky");
  public static irishWhiskey = new EnumAlcoholType(8, "IrishWhiskey", "enum_alcohol_type_irish_whiskey");
  public static traditionalAfricanBeer = new EnumAlcoholType(9, "TraditionalAfricanBeer", "enum_alcohol_type_traditional_african_beer");
  public static brandy = new EnumAlcoholType(13, "Brandy", "enum_alcohol_type_brandy");
  public static spiritsFromGrape = new EnumAlcoholType(14, "SpiritsFromGrape", "enum_alcohol_type_spirits_from_grape");
  public static whisky = new EnumAlcoholType(15, "Whisky", "enum_alcohol_type_whisky");
  public static rumAndSugarSpirits = new EnumAlcoholType(16, "RumAndSugarSpirits", "enum_alcohol_type_rum_and_sugar_spirits");
  public static gin = new EnumAlcoholType(17, "Gin", "enum_alcohol_type_gin");
  public static vodka = new EnumAlcoholType(18, "Vodka", "enum_alcohol_type_vodka");
  public static other = new EnumAlcoholType(19, "Other", "enum_alcohol_type_other");
  public static zeroPercent = new EnumAlcoholType(20, "ZeroPercent", "enum_alcohol_type_zero_percent");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumAlcoholType.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isNone() { return this === EnumAlcoholType.none; }
  public isBeer() { return this === EnumAlcoholType.beer; }
  public isStillCiderPerry() { return this === EnumAlcoholType.stillCiderPerry; }
  public isSparklingCiderPerry() { return this === EnumAlcoholType.sparklingCiderPerry; }
  public isStillWine() { return this === EnumAlcoholType.stillWine; }
  public isSparklingWine() { return this === EnumAlcoholType.sparklingWine; }
  public isSpirits() { return this === EnumAlcoholType.spirits; }
  public isScotchWhisky() { return this === EnumAlcoholType.scotchWhisky; }
  public isIrishWhiskey() { return this === EnumAlcoholType.irishWhiskey; }
  public isTraditionalAfricanBeer() { return this === EnumAlcoholType.traditionalAfricanBeer; }
  public isBrandy() { return this === EnumAlcoholType.brandy; }
  public isSpiritsFromGrape() { return this === EnumAlcoholType.spiritsFromGrape; }
  public isWhisky() { return this === EnumAlcoholType.whisky; }
  public isRumAndSugarSpirits() { return this === EnumAlcoholType.rumAndSugarSpirits; }
  public isGin() { return this === EnumAlcoholType.gin; }
  public isVodka() { return this === EnumAlcoholType.vodka; }
  public isOther() { return this === EnumAlcoholType.other; }
  public isZeroPercent() { return this === EnumAlcoholType.zeroPercent; }

  public static getValues(): readonly EnumAlcoholType[] {
    return EnumAlcoholType.allValues;
  }

  public static getValuesExcept0(): readonly EnumAlcoholType[] {
    return EnumAlcoholType.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumAlcoholType {
    const found = EnumAlcoholType.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumAlcoholType - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumAlcoholType): EnumAlcoholType {
    const found = name ? EnumAlcoholType.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumAlcoholType - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumAlcoholType>): boolean {
    return this.key === other?.key;
  }
}